import { fetchWithAuthRetry } from '@/lib/utils/fetchWithAuthRetry'

/**
 * The one browser → /api helper.
 *
 * Authenticated pages read their data through route handlers, not through
 * the browser Supabase client: the route reads the session from cookies, so
 * no Web Lock is involved and one stuck tab cannot starve the others
 * (2026-09-17). Cookies always travel; a 401 gets exactly one coordinated
 * session recovery via fetchWithAuthRetry; a non-2xx body becomes ApiError.
 */
export class ApiError extends Error {
  readonly status: number
  readonly body: unknown
  constructor(status: number, message: string, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetchWithAuthRetry(url, { credentials: 'include', ...init })

  if (!response.ok) {
    let body: unknown = undefined
    try { body = await response.json() } catch { /* not JSON */ }
    const serverMessage = (body as { error?: unknown } | undefined)?.error
    const message = typeof serverMessage === 'string' ? serverMessage : `Request failed: ${response.status}`
    throw new ApiError(response.status, message, body)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}
